import { useState, useEffect, useRef } from "react";
import {
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  styled,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CollapsibleSection from "./CollapsibleSection";
import { useCheckoutSimplifiedUI } from "../../CheckoutSimplifiedUIProvider";
import { formatMoney } from "@brandclub/common-ui";
import type { FulfillmentOption } from "../../types";

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "0",
  paddingBottom: "8px",
});

const OptionContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "12px",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "8px",
  cursor: "pointer",
  transition: "all 0.2s",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  "&.selected": {
    borderColor: theme.palette.primary.main,
    borderWidth: "2px",
    backgroundColor: theme.palette.action.selected,
  },
}));

const OptionHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const OptionDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flex: 1,
});

const OptionTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: "14px",
});

const OptionSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  color: theme.palette.text.secondary,
}));

const OptionPrice = styled(Typography)({
  fontWeight: 600,
  fontSize: "14px",
  whiteSpace: "nowrap",
});

const PlaceholderMessage = styled(Box)(({ theme }) => ({
  padding: "20px 16px",
  backgroundColor: "#f6f8fa",
  borderRadius: "8px",
  textAlign: "center",
  color: theme.palette.text.secondary,
  fontSize: "14px",
  margin: "16px 0",
}));

const ShippingNote = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  color: theme.palette.text.secondary,
  marginTop: "8px",
  paddingBottom: "4px",
}));

interface ShippingMethodSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  onComplete: () => void;
  disabled?: boolean;
}

export const ShippingMethodSection = ({
  isOpen,
  onToggle,
  onComplete,
  disabled = false,
}: ShippingMethodSectionProps) => {
  const { session, selectFulfillmentOption, updatingFulfillment } =
    useCheckoutSimplifiedUI();
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const successTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout>(undefined);

  // Initialize selected option from session
  useEffect(() => {
    if (session?.fulfillment_option_id) {
      setSelectedOption(session.fulfillment_option_id);
    }
  }, [session?.fulfillment_option_id]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
      }
    };
  }, []);

  const handleSelect = async (optionId: string) => {
    setSelectedOption(optionId);
    try {
      await selectFulfillmentOption(optionId);
      onComplete();

      // Show success checkmark
      setShowSuccess(true);
      setIsFadingOut(false);

      // Clear any existing timeouts
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
      }

      // Start fade out after 2 seconds
      successTimeoutRef.current = setTimeout(() => {
        setIsFadingOut(true);

        // Remove from DOM after fade completes
        fadeOutTimeoutRef.current = setTimeout(() => {
          setShowSuccess(false);
          setIsFadingOut(false);
        }, 500); // Match the transition duration
      }, 2000);
    } catch (error) {
      console.error("Failed to select fulfillment option:", error);
    }
  };

  const formatDeliveryEstimate = (option: FulfillmentOption): string => {
    if (option.type === "digital") {
      return option.subtitle || "Available immediately";
    }

    if (option.type === "shipping" && option.subtitle) {
      return option.subtitle;
    }

    if (
      option.type === "shipping" &&
      option.earliest_delivery_time &&
      option.latest_delivery_time
    ) {
      const earliest = new Date(
        option.earliest_delivery_time
      ).toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const latest = new Date(option.latest_delivery_time).toLocaleDateString(
        "en-US",
        { month: "short", day: "numeric" }
      );
      return `${earliest} - ${latest}`;
    }

    return "";
  };

  const options = session?.fulfillment_options || [];
  const hasShippingAddress = !!session?.fulfillment_address;

  return (
    <CollapsibleSection
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={disabled}
      title="Shipping method"
      titleIcon={
        updatingFulfillment ? (
          <CircularProgress
            size={16}
            thickness={4}
            sx={{
              color: "text.secondary",
              opacity: 0.6,
            }}
          />
        ) : showSuccess ? (
          <CheckCircleIcon
            sx={{
              fontSize: 20,
              color: "success.main",
              opacity: isFadingOut ? 0 : 1,
              transform: isFadingOut ? "scale(0.8)" : "scale(1)",
              transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
              animation: isFadingOut ? "none" : "fadeIn 0.3s ease-in",
              "@keyframes fadeIn": {
                from: { opacity: 0, transform: "scale(0.8)" },
                to: { opacity: 1, transform: "scale(1)" },
              },
            }}
          />
        ) : null
      }
      hideControl={false}
    >
      {!hasShippingAddress || options.length === 0 ? (
        <Box>
          <PlaceholderMessage>
            Enter your shipping address to view available shipping methods.
          </PlaceholderMessage>
          <ShippingNote>
            Orders placed after 12pm EST may take an additional day to ship
          </ShippingNote>
        </Box>
      ) : (
        <FormContainer>
          <RadioGroup
            value={selectedOption}
            onChange={(e) => handleSelect(e.target.value)}
            sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {options.map((option) => (
              <OptionContainer
                key={option.id}
                className={selectedOption === option.id ? "selected" : ""}
                onClick={() =>
                  !disabled && !updatingFulfillment && handleSelect(option.id)
                }
              >
                <OptionHeader>
                  <FormControlLabel
                    value={option.id}
                    control={<Radio />}
                    label=""
                    disabled={disabled || updatingFulfillment}
                    sx={{ margin: 0 }}
                  />
                  <OptionDetails>
                    <OptionTitle>
                      {option.title}
                      {option.type === "shipping" &&
                        option.carrier &&
                        ` (${option.carrier})`}
                    </OptionTitle>
                    <OptionSubtitle>
                      {formatDeliveryEstimate(option)}
                    </OptionSubtitle>
                  </OptionDetails>
                  <OptionPrice>
                    {option.total === 0 ? "FREE" : formatMoney(option.total)}
                  </OptionPrice>
                </OptionHeader>
              </OptionContainer>
            ))}
          </RadioGroup>
          <ShippingNote>
            Orders placed after 12pm EST may take an additional day to ship
          </ShippingNote>
        </FormContainer>
      )}
    </CollapsibleSection>
  );
};
